import { useState, useEffect, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";

import queryString from "query-string";

import getCrimes from "@app/services/cases/getCrimes";
import getCourts from "@app/services/cases/getCourts";

import { useToast } from "@hooks/useToast";

import { DATA_SOURCE_TYPE } from "@utils/enum";

import { getCrimesFormConfig, parseInitialData } from "./CrimesForm.utils";

export const useCrimesForm = ({ sourceDataType, data, onSubmit }) => {
  const { messages } = useIntl();
  const { showErrorToast } = useToast();

  const [crimesData, setCrimesData] = useState([]);
  const [courtsData, setCourtsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getCrimesData = useCallback(
    async (query = "") => {
      try {
        setIsLoading(true);

        const sourceDataTypesParam = ["B"];

        if (sourceDataType === DATA_SOURCE_TYPE.ACTAPUBLICA_ROLE_ANALYSIS) {
          sourceDataTypesParam.push("SF");
        }

        const queryFilters = {
          "filter[search][columns]": "id,name",
          "filter[search][value]": query,
          "filter[type]": sourceDataTypesParam,
        };

        const completeQuery = query
          ? queryString.stringify(queryFilters, {
              skipNull: true,
              arrayFormat: "comma",
            })
          : "";

        const { data: crimes } = await getCrimes(completeQuery);

        setCrimesData(crimes);
      } catch {
        showErrorToast(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    },
    [sourceDataType, showErrorToast, messages.exception_error_message]
  );

  const getCourtsData = useCallback(
    async (query = "") => {
      try {
        setIsLoading(true);

        const completeQuery = query
          ? queryString.stringify(
              {
                "filter[search][columns]": "id,court_name",
                "filter[search][value]": query,
              },
              { skipNull: true }
            )
          : "";

        const { data: courts } = await getCourts(completeQuery);

        setCourtsData(courts);
      } catch {
        showErrorToast(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    },
    [showErrorToast, messages.exception_error_message]
  );

  const handleSearchCrime = useCallback(
    event => {
      const { query = "" } = event;

      getCrimesData(query);
    },
    [getCrimesData]
  );

  const handleSearchCourt = useCallback(
    event => {
      const { query = "" } = event;

      getCourtsData(query);
    },
    [getCourtsData]
  );

  const config = useMemo(
    () =>
      getCrimesFormConfig(
        {
          crimesData,
          onSearchCrime: handleSearchCrime,
        },
        {
          courtsData,
          onSearchCourt: handleSearchCourt,
        }
      ),
    [crimesData, handleSearchCrime, courtsData, handleSearchCourt]
  );

  const initialData = useMemo(() => parseInitialData(data), [data]);

  const handleSubmit = useCallback(
    values => {
      onSubmit({ ...data, ...values });
    },
    [data, onSubmit]
  );

  useEffect(() => {
    const abortController = new AbortController();

    const fetchDetails = async () => {
      try {
        setIsLoading(true);

        const [{ data: crimes }, { data: courts }] = await Promise.all([
          getCrimes("", { signal: abortController.signal }),
          getCourts("", { signal: abortController.signal }),
        ]);

        setCrimesData(crimes);
        setCourtsData(courts);
      } catch {
        showErrorToast(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();

    return () => abortController.abort();
  }, [messages.exception_error_message, showErrorToast]);

  return {
    isLoading,
    config,
    initialData,
    handleSubmit,
  };
};
